package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/udf")
@Composable
fun ComposeUDFPage() {
    BlogLayout(
        PageProperties(
            "Unidirectional data flow (UDF)",
            "Unidirectional Data Flow (UDF), also known as State Hoisting, lifting state up (data goes down, actions go up) for better reusability and clean architecture."
        ),
        BlogProperties("Jetpack Compose", "Unidirectional data flow (UDF)")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UDF means state flows down from ViewModel to Composables and events flow up from Composables to ViewModel. The ViewModel holds the source of truth, updates it in response to events, and Composables re-render based on the new state. This makes state predictable and easy to debug.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UDF as a one-way street for your “data goes down, actions go up. In Compose, it means managing your UI state clearly and predictably. (UDF in compose is an approach to app development whereby state stored in a composable should not be directly changed by any child composable functions.)",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The key point to note about UDF is that the value assigned to component is only changed from within parent composable and never directly updated by child.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Unidirectional Data Flow (UDF), also known as State Hoisting, is a prevalent architectural pattern where the state descends and the events ascend, leading to a uni-directional stream of information.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Jetpack Compose employs this methodology to segregate composables, or components responsible for UI state representation, from those that manage state storage and modification.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State flows from a single source (usually the ViewModel) down to your compostables, and user interactions trigger events that flow back up to the ViewModel for handling.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                SpanText(
                    text = "In a UDF configured system, the user interaction loop proceeds as follows.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Event",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Part of the UI generates an event and passes it upward, such as a button click passed to the ViewModel to handle; or an event is passed from other layers of your app, such as indicating that the user session has expired.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Update state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "An event handler might change the state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Display state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )
                }

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "The state holder passes down the state, and the UI displays it.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Events that either originate from the UI (button clicks, text entry, etc) or elsewhere (API Calls, Callbacks, etc) are processed by a handler that then in turn updates the UI State that is passed into Composable Functions. Since Composables are Stateless, the provided UI State will be used to build the UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "UDF Flow",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State flows down: to the Composables. Managed by the parent. Data is passed from higher-level composables (or state holders like ViewModels) to lower-level ones that display it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Events flow up: to the State holder. Triggered by the child and sent to the parent for handling. When something needs to change (e.g., a button click), the lower-level composable sends an event upwards to the state owner, which then processes the event and potentially updates the state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Pattern Data flows down, events flow up",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel exposes state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Composables observe state",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "UI events bubble up via callbacks",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    //1 Parent owns state.
                    //2 State is passed down to child.
                    //3 Child emits events back to parent.
                    
                    @Composable
                    fun ParentCounter() {
                        //1.Parent owns state.
                        var count by remember { mutableStateOf(0) }
                        
                        //State is passed down to child.
                        Counter(count = count, onIncrement = { count++ })
                    }

                    @Composable
                    fun Counter(count: Int, onIncrement: () -> Unit) {
                        Column {
                            Text("Count: $count")
                            
                            //3. Child emits events back to parent.
                            Button(onClick = onIncrement) {
                                Text("Increment")
                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Advantage UDF",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Clarity and Consistency: Your UI reflects the state accurately, and changes are easy to track.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Improved Reusability: Composable functions become independent of specific state instances, making them more reusable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Enhanced Testability: Testing isolated composables with fake state becomes much easier.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Reduced Bugs: Fewer opportunities for side effects and unexpected behavior in composables.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Implementing UDF 3 ways",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State Hoisting — Keep your state somewhere (usually in the ViewModel) and hoist it to the necessary composable.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Event Handling — Pass event callbacks (onClick, etc.) as parameters to your composables. Let them trigger actions in the ViewModel, not modify the state directly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Single Source of Truth — The ViewModel is the state’s master. All changes should happen there.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Relation between State Hoisting and UDF",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State hoisting has a similar meaning in that it involves moving state from a child composable up to the calling (parent) composable or a higher ancestor.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "When the child composable is called by the parent, it is passed the state along with an event handler. With State hoisting in Compose, actually, we can say we do the UDF approach correctly in Compose.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "We send events with the current state upwards, update the state by event, and then get the updated state.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Hoisted Task Input",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "Hoisted Task Input",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The composable becomes stateless and only accepts the state to display (value: T) and events to request a change (onValueChange: (T) -> Unit).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    /**
                     * Data model for a single task. We prefer immutable data classes.
                     */
                    data class Task(val id: Int, val title: String, val isComplete: Boolean = false)

                    @Composable
                    fun TaskManagerScreen() {
                        // State is hoisted up to the parent screen composable
                        var newTaskText by rememberSaveable { mutableStateOf("") } // Use rememberSaveable for config changes

                        Column {
                            TaskInput(
                                // State flows down
                                currentInput = newTaskText,
                                
                                // Events flow up
                                // callback function
                                // Triggered every time the user interacts
                                // Passed the new value as a parameter
                                
                                //it (or newText) is the latest value from the Ul
                                //value = it updates your state
                                //Updating state triggers recomposition
                                onInputChanged = { newText ->
                                    newTaskText = newText // Parent updates the state
                                },
                                
                                onAddTask = {
                                    // Logic to add the task would go here
                                    newTaskText = "" // Clear the input after adding
                                }
                            )
                            // ... other components like a LazyColumn for the task list
                        }
                    }

                    @Composable
                    fun TaskInput(
                        currentInput: String,
                        onInputChanged: (String) -> Unit,
                        onAddTask: () -> Unit
                    ) {
                        // This composable is now stateless. It only draws the UI based on inputs.
                        OutlinedTextField(
                            value = currentInput,
                            onValueChange = onInputChanged, // Passes the event (new text) up to the parent
                            label = { Text("Enter a new task...") },
                            trailingIcon = {
                                Button(
                                    onClick = onAddTask, // Passes the event (add task click) up to the parent
                                    enabled = currentInput.isNotBlank()
                                ) {
                                    Text("Add")
                                }
                            }
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Why it doesn’t update the UI?",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "It just changes the value of the variable, but our UI needs to refresh.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For auto UI refresh, we need state objects. If we use a state object, our UI will get refreshed automatically when data gets changed. Let’s test the same code with the state object.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The composable are subscribed to a state, and when the value of the state is updated then all the composable who are subscribed to it also update the value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    //With State Object

                    @Composable
                    fun MutableStateClick() {
                        var clickCount by mutableStateOf(0)//Not recommended
                        Column {
                            Button(onClick = { clickCount++ }) {
                                Text(text = "" + clickCount + " times clicked")
                            }
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}